import { FormGroup, FormParagraph, FormSection } from '@ksyos/design-system';
import type { ValidationErrors } from '../../../../utils/validation';
import type { IServiceProvider } from '../../../interfaces/serviceProvider';
import type {
    IPersonSettingCarePathServiceConsumerSettings,
    PersonalSettingsCarePathServiceConsumerFormMode,
} from './PersonalSettingsCarePathServiceConsumerSetting';
import type { PersonalSettingsCarePathServiceProviderFormMode } from './PersonalSettingsCarePathServiceProviderSetting';
import { ServiceCode } from '../../../../../common/enums/serviceCode';
import { SpirometerSoftwareNames } from '../../../../../common/enums/spirometerSoftwareNames';
import { translateKey } from '../../../../translate';
import { InputTriState } from '../../../elements/common';
import { SearchableEnumDropdown } from '../../../elements/SearchableEnumDropdown';

interface ISoftwareSettingsProps {
    errors: ValidationErrors;
    handleChange: HandleChange;
    mode: PersonalSettingsCarePathServiceConsumerFormMode | PersonalSettingsCarePathServiceProviderFormMode;
    service: {
        code?: ServiceCode;
    };
    serviceRightType: 'ServiceConsumer' | 'ServiceProvider';
    spirometerSoftwareName?: string;
    usingRestEcgMeter?: boolean;
    usingSpirometer?: boolean;
    validateOne: ValidateOne;
}

type HandleChange =
    | ((
          key: keyof IPersonSettingCarePathServiceConsumerSettings,
          value: IPersonSettingCarePathServiceConsumerSettings[keyof IPersonSettingCarePathServiceConsumerSettings]
      ) => void)
    | ((key: keyof IServiceProvider, value: IServiceProvider[keyof IServiceProvider]) => void);

type ValidateOne =
    | ((key: keyof IPersonSettingCarePathServiceConsumerSettings) => void)
    | ((key: keyof IServiceProvider) => void);

export function SoftwareSettings(props: ISoftwareSettingsProps) {
    const serviceTypesToRender =
        props.serviceRightType === 'ServiceProvider'
            ? [ServiceCode.TPO, ServiceCode.TCOR]
            : [ServiceCode.TPC, ServiceCode.TCCi, ServiceCode.TCCh, ServiceCode.TCCr];

    if (!props.service.code || !serviceTypesToRender.includes(props.service.code)) {
        return null;
    }

    return (
        <FormSection title={translateKey('addCarePathSettingsHeaders.software')}>
            {(isServiceProviderForServiceCode([ServiceCode.TPO]) ||
                isServiceConsumerForServiceCode([ServiceCode.TPC])) && (
                <>
                    <FormGroup label={translateKey('addCarePathSettingsLabelsServiceProvider.usingSpirometer')}>
                        {props.mode === 'read' ? (
                            <FormParagraph>
                                {props.usingSpirometer ? translateKey('common.yes') : translateKey('common.no')}
                            </FormParagraph>
                        ) : (
                            <InputTriState
                                fieldKey="usingSpirometer"
                                notNullable
                            />
                        )}
                    </FormGroup>

                    {props.usingSpirometer && (
                        <FormGroup
                            label={translateKey('addCarePathSettingsLabelsServiceProvider.spirometerSoftwareName')}
                        >
                            {props.mode === 'read' ? (
                                <FormParagraph>{props.spirometerSoftwareName || '-'}</FormParagraph>
                            ) : (
                                <>
                                    <SearchableEnumDropdown
                                        enumTranslationName="spirometerSoftwareNames"
                                        error={props.errors.get('spirometerSoftwareName')}
                                        jsEnum={SpirometerSoftwareNames}
                                        onChange={(value) =>
                                            props.handleChange('spirometerSoftwareName', value || undefined)
                                        }
                                        placeholder={translateKey('serviceProvider.selectSpirometerSoftwareName')}
                                        value={props.spirometerSoftwareName as SpirometerSoftwareNames}
                                    />
                                </>
                            )}
                        </FormGroup>
                    )}
                </>
            )}

            {(isServiceProviderForServiceCode([ServiceCode.TCOR]) ||
                isServiceConsumerForServiceCode([ServiceCode.TCCi, ServiceCode.TCCh, ServiceCode.TCCr])) && (
                <FormGroup label={translateKey('addCarePathSettingsLabelsServiceProvider.usingRestEcgMeter')}>
                    {props.mode === 'read' ? (
                        <FormParagraph>
                            {props.usingRestEcgMeter ? translateKey('common.yes') : translateKey('common.no')}
                        </FormParagraph>
                    ) : (
                        <InputTriState
                            fieldKey="usingRestEcgMeter"
                            notNullable
                        />
                    )}
                </FormGroup>
            )}
        </FormSection>
    );

    function isServiceProviderForServiceCode(serviceCodes: ServiceCode[]) {
        return (
            props.serviceRightType === 'ServiceProvider' &&
            props.service.code &&
            serviceCodes.includes(props.service.code)
        );
    }

    function isServiceConsumerForServiceCode(serviceCodes: ServiceCode[]) {
        return (
            props.serviceRightType === 'ServiceConsumer' &&
            props.service.code &&
            serviceCodes.includes(props.service.code)
        );
    }
}
